import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Sparkles, Mail, Heart, Shield } from "lucide-react";
import { Link } from "react-router-dom";
// Import WebP versions for modern browsers, with fallback to original
import heroBg from "@/assets/hero-bg.jpg?format=webp&quality=80";
import heroBgFallback from "@/assets/hero-bg.jpg";
import friendatoLogo from "@/assets/friendato-logo.png?format=webp&quality=85";
import friendatoLogoFallback from "@/assets/friendato-logo.png";

const ComingSoon = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Email required",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase
        .from("waitlist_signups")
        .insert({ email });

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          toast({
            title: "Already registered!",
            description: "You're already on the waitlist. We'll notify you soon!",
          });
        } else {
          throw error;
        }
      } else {
        toast({
          title: "Success!",
          description: "You've been added to the waitlist. We'll notify you at launch!",
        });
        setEmail("");
      }
    } catch (error) {
      console.error("Error signing up:", error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Friendato - AI-Powered Matchmaking | Coming Soon</title>
        <meta name="description" content="Join the waitlist for Friendato - the AI-powered matchmaking platform helping you find meaningful connections through intelligent conversations and personality matching." />
        <link rel="canonical" href="https://friendato.ai/" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Friendato - AI-Powered Matchmaking | Coming Soon" />
        <meta property="og:description" content="Join the waitlist for the future of dating. AI-powered matchmaking for meaningful connections." />
        <meta property="og:url" content="https://friendato.ai/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://friendato.ai/og-image.png" />
        <meta property="og:site_name" content="Friendato" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Friendato - AI-Powered Matchmaking | Coming Soon" />
        <meta name="twitter:description" content="Join the waitlist for AI-powered matchmaking." />
        <meta name="twitter:image" content="https://friendato.ai/og-image.png" />
        
        {/* Additional SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="keywords" content="AI matchmaking, dating app, halal dating, muslim matchmaking, find partner, relationship, UAE dating" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Friendato",
            "url": "https://friendato.ai",
            "description": "AI-powered matchmaking platform for meaningful connections",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://friendato.ai/blog?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Friendato",
            "url": "https://friendato.ai",
            "logo": "https://friendato.ai/pwa-512x512.png",
            "sameAs": [],
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer support",
              "email": "support@friendato.ai"
            }
          })}
        </script>
      </Helmet>
      <div className="min-h-screen bg-background">
      {/* Simple Logo Header */}
      <div className="absolute top-6 left-6 z-20">
        <div className="flex items-center gap-3">
          <picture>
            <source srcSet={friendatoLogo} type="image/webp" />
            <img 
              src={friendatoLogoFallback} 
              alt="Friendato" 
              className="w-10 h-10 rounded-xl object-cover"
              width={40}
              height={40}
            />
          </picture>
          <span className="font-bold text-xl bg-gradient-accent bg-clip-text text-transparent">Friendato</span>
        </div>
      </div>
      
      <section className="relative min-h-[100svh] flex items-center justify-center overflow-hidden">
        {/* Enhanced Background with overlay */}
        <div className="absolute inset-0 z-0">
          <picture>
            <source srcSet={heroBg} type="image/webp" />
            <img 
              src={heroBgFallback} 
              alt="Background" 
              className="w-full h-full object-cover opacity-10"
              loading="eager"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-hero" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background" />
        </div>

        {/* Animated Geometric Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, hsl(var(--primary)) 35px, hsl(var(--primary)) 36px)`,
          }} />
        </div>

        {/* Glow Effects */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[128px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/20 rounded-full blur-[128px] animate-pulse delay-1000" />

        {/* Content */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-3 px-6 sm:px-8 py-3 rounded-full bg-primary/10 border-2 border-primary/50 mb-6 sm:mb-8 animate-fade-in shadow-glow backdrop-blur-sm">
              <Sparkles className="w-5 h-5 text-primary animate-pulse" />
              <span className="text-lg sm:text-xl font-bold text-primary">{t('waitlist.comingSoon')}</span>
            </div>

            {/* Main Heading with enhanced styling */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-extrabold mb-6 sm:mb-8 bg-gradient-accent bg-clip-text text-transparent leading-[1.1] px-2 animate-fade-in">
              {t('hero.title')}
            </h1>

            {/* Subtitle with better contrast */}
            <p className="text-lg sm:text-xl md:text-2xl text-foreground/80 mb-8 sm:mb-10 max-w-3xl mx-auto px-4 leading-relaxed animate-fade-in font-medium">
              {t('waitlist.subtitle')}
            </p>

            {/* Email Signup Form - Enhanced */}
            <form onSubmit={handleSubmit} className="max-w-xl mx-auto mb-12 sm:mb-16 px-4 animate-fade-in">
              <div className="flex flex-col sm:flex-row gap-4 p-2 rounded-2xl bg-gradient-to-r from-card/50 to-card/30 backdrop-blur-xl border border-border/50 shadow-glow">
                <div className="relative flex-1">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                  <Input
                    type="email"
                    placeholder={t('waitlist.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-12 h-14 bg-background/80 backdrop-blur border-border/50 focus:border-primary/50 focus:ring-2 focus:ring-primary/20 text-base rounded-xl transition-all"
                    disabled={loading}
                  />
                </div>
                <Button 
                  type="submit" 
                  size="lg"
                  className="h-14 px-8 bg-gradient-accent hover:opacity-90 text-white font-semibold rounded-xl shadow-soft hover:shadow-glow transition-all hover:scale-[1.02] active:scale-[0.98]"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t('waitlist.joining')}
                    </span>
                  ) : (
                    t('waitlist.joinWaitlist')
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-3">{t('waitlist.disclaimer')}</p>
            </form>

            {/* Trust Indicators - Enhanced Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mt-12 sm:mt-16 px-2 max-w-4xl mx-auto">
              <div className="group relative p-6 rounded-2xl bg-gradient-card backdrop-blur-xl border border-border/50 shadow-soft hover:shadow-glow transition-all hover:scale-[1.02] hover:border-primary/30">
                <div className="absolute inset-0 rounded-2xl bg-gradient-accent opacity-0 group-hover:opacity-5 transition-opacity" />
                <div className="relative flex flex-col items-center gap-3 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <span className="text-sm font-medium text-foreground">{t('hero.botSimulation')}</span>
                </div>
              </div>
              
              <div className="group relative p-6 rounded-2xl bg-gradient-card backdrop-blur-xl border border-border/50 shadow-soft hover:shadow-glow transition-all hover:scale-[1.02] hover:border-accent/30">
                <div className="absolute inset-0 rounded-2xl bg-gradient-accent opacity-0 group-hover:opacity-5 transition-opacity" />
                <div className="relative flex flex-col items-center gap-3 text-center">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                    <Heart className="w-6 h-6 text-accent" />
                  </div>
                  <span className="text-sm font-medium text-foreground">{t('hero.aiAnalysis')}</span>
                </div>
              </div>
              
              <div className="group relative p-6 rounded-2xl bg-gradient-card backdrop-blur-xl border border-border/50 shadow-soft hover:shadow-glow transition-all hover:scale-[1.02] hover:border-secondary/30">
                <div className="absolute inset-0 rounded-2xl bg-gradient-accent opacity-0 group-hover:opacity-5 transition-opacity" />
                <div className="relative flex flex-col items-center gap-3 text-center">
                  <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                    <Sparkles className="w-6 h-6 text-secondary" />
                  </div>
                  <span className="text-sm font-medium text-foreground">{t('hero.fullInsights')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer with Internal Links for SEO */}
      <footer className="relative z-10 py-8 border-t border-border/30 bg-background/50 backdrop-blur-sm">
        <div className="container mx-auto px-4">
          <nav className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-4" aria-label="Footer navigation">
            <Link to="/blog" className="text-sm text-muted-foreground hover:text-primary transition-colors">Blog</Link>
            <Link to="/events" className="text-sm text-muted-foreground hover:text-primary transition-colors">Events</Link>
            <Link to="/success-stories" className="text-sm text-muted-foreground hover:text-primary transition-colors">Success Stories</Link>
            <Link to="/careers" className="text-sm text-muted-foreground hover:text-primary transition-colors">Careers</Link>
            <Link to="/faq" className="text-sm text-muted-foreground hover:text-primary transition-colors">FAQ</Link>
            <Link to="/safety" className="text-sm text-muted-foreground hover:text-primary transition-colors">Safety</Link>
            <Link to="/privacy" className="text-sm text-muted-foreground hover:text-primary transition-colors">Privacy</Link>
            <Link to="/terms" className="text-sm text-muted-foreground hover:text-primary transition-colors">Terms</Link>
          </nav>
          <p className="text-xs text-muted-foreground text-center">© 2025 Friendato. All rights reserved.</p>
        </div>
      </footer>
      </div>
    </>
  );
};

export default ComingSoon;
